import React, { useMemo } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { SelectedCellInfo, useChiPhiPaste } from '../hooks';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { getTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface ChiPhiTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams<{ uuid?: string | null }>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export const ChiPhiTab: React.FC<ChiPhiTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const columns = useMemo(() => getTableColumns(onCellValueChange, formMode), [onCellValueChange, formMode]);

  useChiPhiPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      showIndexColumn
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns as MuaHangRecord[]}
      getRowId={row => row?.uuid || ''}
      pasteTarget={selectedCell}
      actionLoading={isPasting}
      actionsDisabled={formMode === 'view'}
      actionOrder={[
        'add',
        'delete',
        'copy',
        'paste',
        'moveUp',
        'moveDown',
        'export',
        'pin',
        'viewInventory',
        'viewReceipt'
      ]}
      actions={{
        add: onAddRow,
        delete: onDeleteRow,
        copy: onCopyRow,
        paste: onPasteRow,
        moveUp: () => onMoveRow('up'),
        moveDown: () => onMoveRow('down'),
        export: () => console.log('Export clicked'),
        pin: () => console.log('Pin clicked'),
        viewInventory: () => console.log('View inventory clicked'),
        viewReceipt: () => console.log('View receipt clicked')
      }}
    />
  );
};
